import { useAppNavigate } from "hooks/useAppNavigate";
import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import { useLoginPage } from "hooks/useLoginPage";
import { useText } from "context/TextProvider";
import PageMeta from "components/PageMeta";
import { mediaUrl } from "services/strapi.service";
import { AnalyticsEvent } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";
import { ColorWithOpacity } from "types";

// `opacity` on these fields is a 0–100 percentage from Strapi; `alpha()` wants 0–1.
const resolveColor = (
  color: ColorWithOpacity | null | undefined,
  fallbackHex: string,
  fallbackOpacityPercent = 100,
): string => {
  const opacityPercent = typeof color?.opacity === "number" ? color.opacity : fallbackOpacityPercent;
  return alpha(color?.hex || fallbackHex, opacityPercent / 100);
};

const DeviceGrantHome = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_DEVICE_HOME_SCREEN);

  const { loginPageConfig } = useLoginPage();

  const goToLogin = () => appNavigate(`/device-grant-flow/login`);
  const logoUrl = mediaUrl(loginPageConfig?.login_page_style?.logo);

  // `labels` (text), this background image, the 2 heading/sub-heading text
  // colors, and the 2 button background colors below come from Strapi —
  // everything else on this screen (button text, layout) is fixed here in
  // code, same as every other page's button/spacing styling.
  const heroImageUrl = mediaUrl(loginPageConfig?.login_page_style?.homePageBackgroundImage);
  const backgroundColor = "#00001c";
  const panelColor = "#1a1a2e";
  const headingTextColor = resolveColor(loginPageConfig?.login_page_style?.homePagePrimaryTextColor, "#ffffff");
  const subHeadingTextColor = resolveColor(
    loginPageConfig?.login_page_style?.homePageSecondaryTextColor,
    "#ffffff",
    85,
  );
  const getStartedButtonBgColor = resolveColor(
    loginPageConfig?.login_page_style?.homePagePrimaryButtonColor,
    "#ffffff",
  );
  // Login button is a translucent ghost fill by default, not solid.
  const loginButtonBgColor = resolveColor(
    loginPageConfig?.login_page_style?.homePageSecondaryButtonColor,
    "#ffffff",
    10,
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        px: "1.5rem",
        pb: "4rem",
        pt: "4rem",
        boxSizing: "border-box",
        color: "#ffffff",
        backgroundImage: heroImageUrl
          ? `url(${heroImageUrl})`
          : `linear-gradient(to bottom, ${panelColor}, ${backgroundColor})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <PageMeta name="Device Grant Home" />
      {logoUrl && (
        <Box component="img" src={logoUrl} alt={text.header.logoAlt} sx={{ maxWidth: "14rem", mb: "2.5rem" }} />
      )}

      <Typography
        component="h1"
        sx={{
          fontWeight: 800,
          fontSize: "1.75rem",
          lineHeight: 1,
          letterSpacing: 0,
          textAlign: "center",
          textTransform: "uppercase",
          color: headingTextColor,
          mb: "1rem",
        }}
      >
        {text.home.headline}
      </Typography>

      <Typography
        component="p"
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          lineHeight: 1,
          letterSpacing: 0,
          textAlign: "center",
          color: subHeadingTextColor,
          mb: "2.5rem",
        }}
      >
        {text.home.subheading}
      </Typography>

      <Box sx={{ width: "100%", maxWidth: "24rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Button
          onClick={goToLogin}
          sx={{
            width: "100%",
            height: "3.5rem",
            borderRadius: "9999px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1rem",
            lineHeight: 1,
            letterSpacing: 0,
            bgcolor: getStartedButtonBgColor,
            color: "#000000",
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
        >
          {text.home.getStartedButton}
        </Button>
        <Button
          onClick={goToLogin}
          sx={{
            width: "100%",
            height: "3.5rem",
            borderRadius: "9999px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1rem",
            lineHeight: 1,
            letterSpacing: 0,
            bgcolor: loginButtonBgColor,
            color: "#ffffff",
            border: "1px solid rgba(255,255,255,0.6)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          {text.home.loginButton}
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(DeviceGrantHome);
